import React from 'react';
import { Link } from 'react-router-dom';
import {
    BookOpen,
    Target,
    Briefcase,
    Award,
    GraduationCap,
    Hospital,
    FlaskConical,
    ChevronRight
} from 'lucide-react';
import Footer from '../../../components/Footer';
import heroimg from '../../../assets/schools/pharma/B pharm Hero.jpg';
import HowToApplySection from '../../../components/HowToApplySection';

// Placeholder for importing images - replace with actual image paths
const heroImage = heroimg;

const DoctorPharmacy = () => {
    const programSections = [
        {
            title: "Program Outcomes",
            items: [
                "Assess patient health problems, review prescriptions, and provide safe, effective, and appropriate medication therapy, actively participating in ward rounds and therapeutic decision-making.",
                "Demonstrate in-depth knowledge of pharmacology, pharmacotherapeutics, and clinical pharmacy, managing drug therapy for a wide range of diseases using evidence-based guidelines.",
                "Understand and apply ethical principles and legal standards governing pharmacy practice, respecting patient confidentiality and informed consent in clinical settings.",
                "Communicate effectively with doctors, patients, and other healthcare professionals, providing patient counseling and medication use education in understandable terms.",
                "Conduct and contribute to clinical trials, pharmacovigilance, and post-marketing surveillance, detecting, assessing, and reporting adverse drug reactions (ADRs).",
                "Apply critical thinking to solve drug-related problems, optimize treatment plans, and manage drug interactions, making sound therapeutic decisions based on clinical evidence."
            ],
            icon: <Target className="text-emerald-700" size={40} />
        },
        {
            title: "Career Prospects",
            items: [
                "Clinical Pharmacist",
                "Community Pharmacist",
          "Hospital/Health System Pharmacist",
                "Pharmaceutical Industry (e.g., regulatory affairs, R&D, marketing)",
                "Academia and Research",
                "Government and Regulatory Agencies"
            ],
            icon: <Briefcase className="text-emerald-700" size={40} />
        }
    ];

    const uniqueSellingPoints = [
        "Clinical Focus",
        "Comprehensive Curriculum",
        "Hospital Internship",
        "Career Versatility",
        "Professional Authority"
    ];

    return (
        <div className="bg-emerald-50">
            <div className="bg-white text-emerald-900 font-sans mx-4 md:mx-8 lg:mx-16 my-4 mt-[70px]">
                {/* Hero Section */}
                <div className="relative bg-gradient-to-r from-emerald-50 to-white">
                    <div className="container mx-auto px-4 py-16 md:py-24 flex flex-col-reverse md:flex-row items-center">
                        <div className="md:w-1/2 space-y-6">
                            <h1 className="text-4xl md:text-5xl font-bold text-emerald-800 leading-tight">
                                Doctor of Pharmacy (Pharm.D)
                                <span className="block text-lg mt-2 font-medium text-emerald-600">
                                    Maya Devi University, Dehradun
                                </span>
                            </h1>
                            <p className="text-emerald-700 text-lg leading-relaxed">
                                The Pharm.D is a six-year postgraduate program approved by the Pharmacy Council of India (PCI), designed to train students in clinical pharmacy and healthcare. It includes five years of academic study and a mandatory one-year hospital internship, focusing on therapeutics, drug interactions, adverse effects, and evidence-based medication use.
                            </p>
                            <div className="flex space-x-4">
                                <Link
                                    to="https://admission.maya.edu.in/"
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-full 
                           transition duration-300 flex items-center group"
                                >
                                    Apply Now
                                    <ChevronRight
                                        className="ml-2 group-hover:translate-x-1 transition"
                                        size={20}
                                    />
                                </Link>
                            </div>
                        </div>
                        <div className="md:w-1/2 mb-8 md:mb-0 m-10">
                            <img
                                src={heroImage}
                                alt="Pharm.D Program"
                                className="rounded-xl shadow-lg hover:scale-105 transition duration-300"
                            />
                        </div>
                    </div>
                </div>

                {/* Eligibility and Program Details */}
                <div className="container mx-auto px-4 py-16 bg-emerald-50">
                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="bg-white p-6 rounded-xl shadow-md">
                            <h2 className="text-2xl font-bold text-emerald-800 mb-4">
                                <GraduationCap className="inline mr-3 text-emerald-600" size={30} />
                                Eligibility Criteria
                            </h2>
                            <ul className="space-y-3 text-emerald-700">
                                <li>• 10+2 with Physics, Chemistry, and Biology (PCB) or Mathematics (PCM) from a recognized board</li>
                                <li>• Minimum 50% marks (or as per university requirements)</li>
                            </ul>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-md">
                            <h2 className="text-2xl font-bold text-emerald-800 mb-4">
                                <Hospital className="inline mr-3 text-emerald-600" size={30} />
                                Program Details
                            </h2>
                            <table className="w-full text-emerald-700">
                                <tbody>
                                    <tr>
                                        <td className="font-semibold">Duration:</td>
                                        <td>6 Years (5 Years Academic + 1 Year Internship)</td>
                                    </tr>
                                    <tr>
                                        <td className="font-semibold">Approved By:</td>
                                        <td>Pharmacy Council of India (PCI)</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Program Sections */}
                <div className="container mx-auto px-4 py-16">
                    <div className="grid md:grid-cols-2 gap-8">
                        {programSections.map((section, index) => (
                            <div
                                key={index}
                                className="bg-emerald-50 p-6 rounded-xl shadow-md hover:shadow-lg transition"
                            >
                                <div className="flex items-center mb-4">
                                    {section.icon}
                                    <h3 className="text-2xl font-bold ml-4 text-emerald-800">
                                        {section.title}
                                    </h3>
                                </div>
                                <ul className="space-y-2 text-emerald-700">
                                    {section.items.map((item, idx) => (
                                        <li key={idx} className="flex items-start">
                                            <span className="mr-2 text-emerald-600">•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                {/* USPs Section */}
                <div className="container mx-auto px-4 py-16">
                    <h2 className="text-3xl font-bold text-center text-emerald-800 mb-12">
                        Why Choose Us
                    </h2>
                    <div className="max-w-3xl mx-auto">
                        <div className="bg-emerald-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition">
                            <div className="text-emerald-600 text-4xl mb-6 flex justify-center">
                                <Award size={60} />
                            </div>
                            <h3 className="text-2xl font-bold text-emerald-800 text-center mb-6">
                                Our USPs
                            </h3>
                            <ul className="space-y-3">
                                {uniqueSellingPoints.map((usp, index) => (
                                    <li
                                        key={index}
                                        className="flex items-center text-emerald-700 text-lg"
                                    >
                                        <div className="mr-3 text-emerald-600">•</div>
                                        {usp}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            {/* How to Apply Section */}
            <HowToApplySection />

            <div className="bg-emerald-50">
                <Footer />
            </div>
        </div>
    );
};

export default DoctorPharmacy;